import { useCallback, useEffect, useRef } from "react";

const stopFocusIn = (e) => e.stopImmediatePropagation();

/** Ref callback for a tree item that stops `focusin` escaping to the tree view,
 * which would otherwise steal focus away from a drag or an inline rename.
 *
 * The returned callback is stable, so React only invokes it when the element
 * mounts or unmounts. An inline ref callback is a new function on every render,
 * which makes React re-run it every time and leaks one listener per render.
 */
export const useFocusInGuardRef = () => {
  const attachedTo = useRef(null);

  const detach = useCallback(() => {
    if (attachedTo.current) {
      attachedTo.current.removeEventListener("focusin", stopFocusIn);
      attachedTo.current = null;
    }
  }, []);

  useEffect(() => detach, [detach]);

  return useCallback(
    (el) => {
      if (el === attachedTo.current) return;
      detach();
      if (el) {
        attachedTo.current = el;
        el.addEventListener("focusin", stopFocusIn);
      }
    },
    [detach]
  );
};

export default useFocusInGuardRef;
